import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from '@radix-ui/react-icons';
import { Text, UnstyledButton } from '@/shared/ui';

interface GroupShowMoreRowProps {
  /** How many columns the single cell must span. */
  colSpan: number;
  /** The group label, woven into the "… em {cidade}" copy. */
  groupLabel: string;
  /** How many rows are still hidden (drives the pluralized count). */
  hiddenCount: number;
  /** Whether the group is currently expanded past ROWS_PER_GROUP. */
  showingAll: boolean;
  /** Toggles the group between its first ROWS_PER_GROUP rows and all rows. */
  onToggle: () => void;
}

/**
 * The "Mostrar mais N imóveis em {cidade}" / "Mostrar menos" row that caps each
 * group at its first `ROWS_PER_GROUP` rows. The text is inset to line up with
 * the row titles (past the thumbnail), and the chevron flips to point up while
 * the group is fully expanded.
 *
 * @param props - The span, label, hidden count, and toggle state/handler.
 * @returns The show-more `<tr>`.
 */
export const GroupShowMoreRow: React.FC<GroupShowMoreRowProps> = ({
  colSpan,
  groupLabel,
  hiddenCount,
  showingAll,
  onToggle,
}) => {
  const { t } = useTranslation('features/properties');

  return (
    <tr data-testid="group-show-more-row" style={{ borderBottom: '1px solid var(--border-ultra-subtle)' }}>
      <td colSpan={colSpan} style={{ padding: 0 }}>
        <UnstyledButton
          type="button"
          data-testid="group-show-more-button"
          onClick={onToggle}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            // Aligns with the title text that starts after the 64px thumbnail.
            paddingLeft: 'calc(var(--space-4) + 64px + var(--space-3))',
            paddingTop: 'var(--space-2-5)',
            paddingBottom: 'var(--space-2-5)',
            background: 'transparent',
            border: 'none',
            color: 'var(--seed-base)',
            fontSize: 'var(--text-body-sm)',
            fontWeight: 'var(--font-semibold)' as unknown as number,
            fontFamily: 'var(--font-family)',
            cursor: 'pointer',
          }}
        >
          <Text
            component="span"
            className="motion-safe:transition-transform"
            style={{ display: 'inline-flex', transform: showingAll ? 'rotate(180deg)' : 'none', transition: 'var(--transition-standard)' }}
          >
            <ChevronDownIcon width={16} height={16} />
          </Text>
          {showingAll
            ? t('list.grouping.showLess')
            : t('list.grouping.showMore', { count: hiddenCount, group: groupLabel })}
        </UnstyledButton>
      </td>
    </tr>
  );
};
